import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, PencilSimple, Power } from '@phosphor-icons/react';
import ExportButton from '../../components/ExportButton';
import SearchableSelect from '../../components/SearchableSelect';
import SuperAdminDeleteButton from '../../components/SuperAdminDeleteButton';

const COL_STATUS = { key: 'status', label: 'Status', format: r => r.status || 'active' };
const ITEMS_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  COL_STATUS,
];
const UOM_COLUMNS = [
  { key: 'name', label: 'Name' },
  COL_STATUS,
];
const LICENSE_COLUMNS = [
  { key: 'name', label: 'Name' },
  COL_STATUS,
];
const STAFF_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email', label: 'Email' },
  { key: 'halt_location', label: 'Halt Location' },
  { key: 'can_select_preferred_pilot', label: 'Can Select Preferred Pilot', format: r => r.can_select_preferred_pilot ? 'Yes' : 'No' },
  COL_STATUS,
];
const LOADMEN_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'emp_id', label: 'Emp/Contract ID' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'halt_location', label: 'Halt Location' },
  { key: 'type', label: 'Type', format: r => (r.type || 'own') === 'contract' ? 'Contract' : 'Own' },
  { key: 'contractor_name', label: 'Contractor' },
  COL_STATUS,
];
const WAITING_RULES_COLUMNS = [
  { key: 'item', label: 'Item' },
  { key: 'uom', label: 'UOM' },
  { key: 'from_qty', label: 'From Qty' },
  { key: 'to_qty', label: 'To Qty' },
  { key: 'allowed_time_minutes', label: 'Allowed (min)' },
  COL_STATUS,
];

function StatusBadge({ status }) {
  const isActive = status === 'active' || !status;
  return (
    <span className={`text-xs uppercase tracking-widest px-2 py-1 ${isActive ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#FF3333]/10 text-[#FF3333]'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function ToggleBtn({ id, status, masterType, onDone }) {
  const isActive = status === 'active' || !status;
  const handleToggle = async () => {
    await api.put(`/admin/toggle-status/${masterType}/${id}`);
    onDone();
  };
  return (
    <button
      onClick={handleToggle}
      className={`p-2 transition-colors ${isActive ? 'hover:bg-[#FF3333]/10 text-[#FF3333]' : 'hover:bg-[#00C853]/10 text-[#00C853]'}`}
      title={isActive ? 'Deactivate' : 'Activate'}
      data-testid={`toggle-${masterType}-${id}`}
    >
      <Power size={16} weight={isActive ? 'regular' : 'bold'} />
    </button>
  );
}

// ===== ITEMS =====
function ItemsTab() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: '' });
  useEffect(() => { fetchItems(); }, []);
  const fetchItems = async () => { try { const { data } = await api.get('/admin/items'); setItems(data); } catch {} };
  const resetForm = () => { setForm({ name: '', category: '' }); setEditing(null); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) { await api.put(`/admin/items/${editing.id}`, form); }
    else { await api.post('/admin/items', form); }
    fetchItems(); setDialogOpen(false); resetForm();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <ExportButton rows={items} columns={ITEMS_COLUMNS} filename="items" testId="export-items-btn" />
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-item-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}><Plus size={16} className="mr-2" />Add Item</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit Item' : 'Add Item'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label className="text-xs uppercase tracking-widest">Name *</Label><Input data-testid="item-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-widest">Category</Label><Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="rounded-none" placeholder="e.g. Garment, Yarn, Fabric" /></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button><Button type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E5E5E5]">
        <Table><TableHeader><TableRow className="bg-[#F3F3F3]"><TableHead className="text-xs uppercase tracking-widest">Name</TableHead><TableHead className="text-xs uppercase tracking-widest">Category</TableHead><TableHead className="text-xs uppercase tracking-widest">Status</TableHead><TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{items.map((item, i) => (<TableRow key={item.id} className={i%2?'bg-[#F3F3F3]/30':''}>
            <TableCell className="font-medium">{item.name}</TableCell><TableCell>{item.category}</TableCell>
            <TableCell><StatusBadge status={item.status} /></TableCell>
            <TableCell className="text-right">
              <button onClick={() => { setEditing(item); setForm({ name: item.name, category: item.category || '' }); setDialogOpen(true); }} className="p-2 hover:bg-[#F3F3F3]"><PencilSimple size={16} /></button>
              <ToggleBtn id={item.id} status={item.status} masterType="items" onDone={fetchItems} />
              <SuperAdminDeleteButton masterType="items" recordId={item.id} recordName={item.name} onDeleted={fetchItems} />
            </TableCell></TableRow>))}
            {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#666] py-8">No items</TableCell></TableRow>}
          </TableBody></Table>
      </div>
    </div>
  );
}

// ===== UOM =====
function UomTab() {
  const [uoms, setUoms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  useEffect(() => { fetchUoms(); }, []);
  const fetchUoms = async () => { try { const { data } = await api.get('/admin/uom'); setUoms(data); } catch {} };
  const resetForm = () => { setName(''); setEditing(null); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) { await api.put(`/admin/uom/${editing.id}`, { name }); }
    else { await api.post('/admin/uom', { name }); }
    fetchUoms(); setDialogOpen(false); resetForm();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <ExportButton rows={uoms} columns={UOM_COLUMNS} filename="uom" testId="export-uom-btn" />
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-uom-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}><Plus size={16} className="mr-2" />Add UOM</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>{editing ? 'Edit UOM' : 'Add Unit of Measure'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label className="text-xs uppercase tracking-widest">Name *</Label><Input data-testid="uom-name-input" value={name} onChange={e => setName(e.target.value)} required className="rounded-none" /></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button><Button type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E5E5E5]">
        <Table><TableHeader><TableRow className="bg-[#F3F3F3]"><TableHead className="text-xs uppercase tracking-widest">Name</TableHead><TableHead className="text-xs uppercase tracking-widest">Status</TableHead><TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{uoms.map((u, i) => (<TableRow key={u.id || u.name} className={i%2?'bg-[#F3F3F3]/30':''}>
            <TableCell className="font-medium">{u.name}</TableCell>
            <TableCell><StatusBadge status={u.status} /></TableCell>
            <TableCell className="text-right">
              <button onClick={() => { setEditing(u); setName(u.name); setDialogOpen(true); }} className="p-2 hover:bg-[#F3F3F3]"><PencilSimple size={16} /></button>
              <ToggleBtn id={u.id} status={u.status} masterType="uom" onDone={fetchUoms} />
              <SuperAdminDeleteButton masterType="uom" recordId={u.id} recordName={u.name} onDeleted={fetchUoms} />
            </TableCell></TableRow>))}
            {uoms.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-[#666] py-8">No UOMs</TableCell></TableRow>}
          </TableBody></Table>
      </div>
    </div>
  );
}

// ===== LICENSE TYPES =====
function LicenseTypesTab() {
  const [types, setTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  useEffect(() => { fetchTypes(); }, []);
  const fetchTypes = async () => { try { const { data } = await api.get('/admin/license-types'); setTypes(data); } catch {} };
  const resetForm = () => { setName(''); setEditing(null); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) { await api.put(`/admin/license-types/${editing.id}`, { name }); }
    else { await api.post('/admin/license-types', { name }); }
    fetchTypes(); setDialogOpen(false); resetForm();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <ExportButton rows={types} columns={LICENSE_COLUMNS} filename="license_types" testId="export-license-btn" />
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-license-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}><Plus size={16} className="mr-2" />Add License Type</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>{editing ? 'Edit License Type' : 'Add License Type'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label className="text-xs uppercase tracking-widest">Name *</Label><Input data-testid="license-name-input" value={name} onChange={e => setName(e.target.value)} required className="rounded-none" /></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button><Button type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E5E5E5]">
        <Table><TableHeader><TableRow className="bg-[#F3F3F3]"><TableHead className="text-xs uppercase tracking-widest">Name</TableHead><TableHead className="text-xs uppercase tracking-widest">Status</TableHead><TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{types.map((t, i) => (<TableRow key={t.id || t.name} className={i%2?'bg-[#F3F3F3]/30':''}>
            <TableCell className="font-medium">{t.name}</TableCell>
            <TableCell><StatusBadge status={t.status} /></TableCell>
            <TableCell className="text-right">
              <button onClick={() => { setEditing(t); setName(t.name); setDialogOpen(true); }} className="p-2 hover:bg-[#F3F3F3]"><PencilSimple size={16} /></button>
              <ToggleBtn id={t.id} status={t.status} masterType="license-types" onDone={fetchTypes} />
              <SuperAdminDeleteButton masterType="license-types" recordId={t.id} recordName={t.name} onDeleted={fetchTypes} />
            </TableCell></TableRow>))}
            {types.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-[#666] py-8">No license types</TableCell></TableRow>}
          </TableBody></Table>
      </div>
    </div>
  );
}

// ===== LOADMEN =====
function LoadmenTab() {
  const [loadmen, setLoadmen] = useState([]);
  const [haltNames, setHaltNames] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', emp_id: '', mobile: '', contractor_name: '', halt_location: '', type: 'own' });
  const [search, setSearch] = useState('');
  useEffect(() => { fetchLoadmen(); fetchHalts(); fetchContractors(); }, []);
  const fetchLoadmen = async () => { try { const { data } = await api.get('/admin/loadmen'); setLoadmen(data); } catch {} };
  const fetchContractors = async () => {
    try { const { data } = await api.get('/admin/helper-contractors', { params: { only_active: true } }); setContractors(data); }
    catch { setContractors([]); }
  };
  const fetchHalts = async () => {
    try {
      const { data } = await api.get('/admin/halt-locations');
      setHaltNames(Array.isArray(data) ? data : []);
    } catch { setHaltNames([]); }
  };
  const resetForm = () => { setForm({ name: '', emp_id: '', mobile: '', contractor_name: '', halt_location: '', type: 'own' }); setEditing(null); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/loadmen/${editing.id}`, form); }
      else { await api.post('/admin/loadmen', form); }
      fetchLoadmen(); setDialogOpen(false); resetForm();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };
  const q = search.trim().toLowerCase();
  const filtered = q
    ? loadmen.filter(l =>
        (l.name || '').toLowerCase().includes(q) ||
        (l.emp_id || '').toLowerCase().includes(q) ||
        (l.mobile || '').toLowerCase().includes(q) ||
        (l.contractor_name || '').toLowerCase().includes(q) ||
        (l.halt_location || '').toLowerCase().includes(q)
      )
    : loadmen;
  const haltOptions = haltNames.map(n => ({ value: n, label: n }));
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 justify-between">
        <Input
          data-testid="loadmen-search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, emp/contract ID or halt location..."
          className="rounded-none max-w-md"
        />
        <div className="flex items-center gap-2">
          <ExportButton rows={filtered} columns={LOADMEN_COLUMNS} filename="loadmen" testId="export-loadmen-btn" />
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild><Button data-testid="add-loadman-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}><Plus size={16} className="mr-2" />Add Helper</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>{editing ? 'Edit Helper' : 'Add Helper'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Name *</Label>
                  <Input data-testid="loadman-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-none" autoFocus />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Emp / Contract ID</Label>
                  <Input data-testid="loadman-emp-input" value={form.emp_id} onChange={e => setForm({...form, emp_id: e.target.value})} className="rounded-none" placeholder="e.g. L-101 or CON-204" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Mobile</Label>
                  <Input data-testid="loadman-mobile-input" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value.replace(/[^0-9+]/g, '')})} className="rounded-none" placeholder="10-digit mobile number" maxLength={15} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Halt Location</Label>
                  <SearchableSelect
                    value={form.halt_location}
                    onChange={v => setForm({...form, halt_location: v})}
                    options={haltOptions}
                    placeholder="Select halt location"
                    searchPlaceholder="Search halt location..."
                    emptyText="No matching halt locations"
                    allowClear
                    testId="loadman-halt-select"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Type *</Label>
                  <div className="flex border border-[#E5E5E5] h-9">
                    {[
                      { value: 'own', label: 'Own' },
                      { value: 'contract', label: 'Contract' },
                    ].map((opt, idx) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm({...form, type: opt.value, contractor_name: opt.value === 'own' ? '' : form.contractor_name})}
                        data-testid={`loadman-type-${opt.value}`}
                        className={`flex-1 text-xs uppercase tracking-widest ${idx > 0 ? 'border-l border-[#E5E5E5]' : ''} ${form.type === opt.value ? 'bg-[#002FA7] text-white' : 'bg-white text-[#0A0A0A]'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {form.type === 'contract' && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Contractor Name *</Label>
                    <SearchableSelect
                      value={form.contractor_name}
                      onChange={v => setForm({ ...form, contractor_name: v })}
                      options={contractors.map(c => ({ value: c.name, label: c.name + (c.mobile ? ` · ${c.mobile}` : '') }))}
                      placeholder="Select contractor"
                      searchPlaceholder="Search contractor..."
                      emptyText="No contractors. Add one in Helper Contractors master first."
                      allowClear
                      testId="loadman-contractor-select"
                    />
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button>
                  <Button data-testid="loadman-save-btn" type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <p className="text-[#666] text-xs">{q ? `${filtered.length} match of ${loadmen.length}` : `${loadmen.length} helpers`}</p>
      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Emp / Contract ID</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Mobile</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Halt Location</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Contractor</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((l, i) => (
              <TableRow key={l.id || l.name} className={i%2?'bg-[#F3F3F3]/30':''}>
                <TableCell className="font-medium">{l.name}</TableCell>
                <TableCell className="font-mono text-sm">{l.emp_id || <span className="text-[#999]">—</span>}</TableCell>
                <TableCell className="font-mono text-sm">{l.mobile || <span className="text-[#999]">—</span>}</TableCell>
                <TableCell>{l.halt_location || <span className="text-[#999]">—</span>}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${(l.type || 'own') === 'contract' ? 'bg-[#FF8C00]/10 text-[#FF8C00]' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>
                    {(l.type || 'own') === 'contract' ? 'Contract' : 'Own'}
                  </span>
                </TableCell>
                <TableCell>{(l.type || 'own') === 'contract' ? (l.contractor_name || <span className="text-[#999]">—</span>) : <span className="text-[#999]">—</span>}</TableCell>
                <TableCell><StatusBadge status={l.status} /></TableCell>
                <TableCell className="text-right">
                  <button onClick={() => { setEditing(l); setForm({ name: l.name, emp_id: l.emp_id || '', mobile: l.mobile || '', contractor_name: l.contractor_name || '', halt_location: l.halt_location || '', type: l.type || 'own' }); setDialogOpen(true); }} className="p-2 hover:bg-[#F3F3F3]" data-testid={`edit-loadman-${l.id}`}><PencilSimple size={16} /></button>
                  <ToggleBtn id={l.id} status={l.status} masterType="loadmen" onDone={fetchLoadmen} />
                  <SuperAdminDeleteButton masterType="loadmen" recordId={l.id} recordName={l.name} onDeleted={fetchLoadmen} />
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-[#666] py-8">{q ? 'No helpers match your search' : 'No helpers'}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ===== STAFF (BOOKING) =====
function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', password: '', halt_location: '', can_select_preferred_pilot: false });
  const [search, setSearch] = useState('');
  // Phase 76x — load halt locations once for the dropdown + filter.
  const [haltOptions, setHaltOptions] = useState([]);
  const [haltFilter, setHaltFilter] = useState('');
  useEffect(() => { fetchStaff(); fetchHalts(); }, []);
  const fetchStaff = async () => { try { const { data } = await api.get('/admin/staff'); setStaff(data); } catch { /* ignore */ } };
  const fetchHalts = async () => {
    try {
      const { data } = await api.get('/admin/halt-locations');
      // Accept either string[] or {name}[] shape.
      const names = (data || []).map(h => typeof h === 'string' ? h : (h?.name || '')).filter(Boolean);
      setHaltOptions(Array.from(new Set(names)).sort());
    } catch { /* ignore */ }
  };
  const resetForm = () => { setForm({ name: '', mobile: '', email: '', password: '', halt_location: '', can_select_preferred_pilot: false }); setEditing(null); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) { await api.put(`/admin/staff/${editing.id}`, { name: form.name, mobile: form.mobile, email: form.email, password: form.password || undefined, halt_location: form.halt_location, can_select_preferred_pilot: form.can_select_preferred_pilot }); }
    else { await api.post('/admin/staff', form); }
    fetchStaff(); setDialogOpen(false); resetForm();
  };
  const q = search.trim().toLowerCase();
  const filtered = staff.filter(s => {
    const matchesQ = !q || (s.name || '').toLowerCase().includes(q) || (s.mobile || '').toLowerCase().includes(q);
    const matchesHalt = !haltFilter || (s.halt_location || '') === haltFilter;
    return matchesQ && matchesHalt;
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            data-testid="staff-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or mobile..."
            className="rounded-none max-w-md"
          />
          {/* Phase 76x — filter by halt location */}
          <select
            data-testid="staff-halt-filter"
            value={haltFilter}
            onChange={e => setHaltFilter(e.target.value)}
            className="rounded-none border border-[#E5E5E5] bg-white px-3 py-2 text-sm"
          >
            <option value="">All halt locations</option>
            {haltOptions.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
        <ExportButton rows={filtered} columns={STAFF_COLUMNS} filename="booking_staff" testId="export-staff-btn" />
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-staff-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}><Plus size={16} className="mr-2" />Add Booking</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>{editing ? 'Edit Booking' : 'Add Booking'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label className="text-xs uppercase tracking-widest">Name *</Label><Input data-testid="staff-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-widest">Mobile * (Login Username)</Label><Input data-testid="staff-mobile-input" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} required className="rounded-none" placeholder="e.g. 9876543210" /></div>
              <div><Label className="text-xs uppercase tracking-widest">Email</Label><Input data-testid="staff-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-widest">{editing ? 'New Password (leave blank to keep current)' : 'Password *'}</Label><Input data-testid="staff-password-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="rounded-none" required={!editing} placeholder={editing ? 'Enter new password to change' : 'Set login password'} /></div>
              {/* Phase 76x — halt location dropdown */}
              <div>
                <Label className="text-xs uppercase tracking-widest">Halt Location</Label>
                <select
                  data-testid="staff-halt-location-select"
                  value={form.halt_location}
                  onChange={e => setForm({...form, halt_location: e.target.value})}
                  className="w-full rounded-none border border-[#E5E5E5] bg-white px-3 py-2 text-sm"
                >
                  <option value="">— Select halt location —</option>
                  {haltOptions.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <p className="text-[10px] text-[#666] mt-1">Tag where this staff books from — admins can filter the staff list and (future) trip reports by halt.</p>
              </div>
              <div className="border-t border-[#E5E5E5] pt-4">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="staff-can-select-preferred-pilot"
                    checked={!!form.can_select_preferred_pilot}
                    onChange={e => setForm({...form, can_select_preferred_pilot: e.target.checked})}
                    className="w-4 h-4 mt-0.5 accent-[#002FA7]"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#0A0A0A]">Can Select Preferred Pilot</p>
                    <p className="text-xs text-[#666] mt-0.5">
                      When OFF (default), this staff's bookings always go through auto-allocation — the Preferred Pilot field is hidden in their booking form.
                      Turn ON only for staff who need manual pilot selection.
                    </p>
                  </div>
                </label>
              </div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button><Button data-testid="staff-save-btn" type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <p className="text-[#666] text-xs">{q ? `${filtered.length} match of ${staff.length}` : `${staff.length} booking staff`}</p>
      <div className="bg-white border border-[#E5E5E5]">
        <Table><TableHeader><TableRow className="bg-[#F3F3F3]"><TableHead className="text-xs uppercase tracking-widest">Name</TableHead><TableHead className="text-xs uppercase tracking-widest">Mobile</TableHead><TableHead className="text-xs uppercase tracking-widest">Email</TableHead><TableHead className="text-xs uppercase tracking-widest">Halt Location</TableHead><TableHead className="text-xs uppercase tracking-widest">Preferred Pilot</TableHead><TableHead className="text-xs uppercase tracking-widest">Status</TableHead><TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{filtered.map((s, i) => (<TableRow key={s.id} className={i%2?'bg-[#F3F3F3]/30':''}>
            <TableCell className="font-medium">{s.name}</TableCell><TableCell>{s.mobile}</TableCell><TableCell>{s.email}</TableCell>
            <TableCell data-testid={`staff-halt-${s.id}`}>
              {s.halt_location
                ? <span className="text-[10px] uppercase tracking-widest bg-[#8E44AD]/10 text-[#8E44AD] px-2 py-1">{s.halt_location}</span>
                : <span className="text-[10px] text-[#999]">—</span>}
            </TableCell>
            <TableCell>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${s.can_select_preferred_pilot ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-[#F3F3F3] text-[#666]'}`} data-testid={`staff-preferred-badge-${s.id}`}>
                {s.can_select_preferred_pilot ? 'Can select' : 'Auto-alloc only'}
              </span>
            </TableCell>
            <TableCell><StatusBadge status={s.status} /></TableCell>
            <TableCell className="text-right">
              <button onClick={() => { setEditing(s); setForm({ name: s.name, mobile: s.mobile || '', email: s.email || '', password: '', halt_location: s.halt_location || '', can_select_preferred_pilot: !!s.can_select_preferred_pilot }); setDialogOpen(true); }} className="p-2 hover:bg-[#F3F3F3]" data-testid={`edit-staff-${s.id}`}><PencilSimple size={16} /></button>
              <ToggleBtn id={s.id} status={s.status} masterType="staff" onDone={fetchStaff} />
              <SuperAdminDeleteButton masterType="staff" recordId={s.id} recordName={s.name} onDeleted={fetchStaff} />
            </TableCell></TableRow>))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-[#666] py-8">{q ? 'No booking staff match your search' : 'No booking staff'}</TableCell></TableRow>}
          </TableBody></Table>
      </div>
    </div>
  );
}

// ===== PICKUP USERS =====
const PICKUP_USER_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'pickup_supplier_name', label: 'Supplier' },
  COL_STATUS,
];

function PickupUsersTab() {
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', pickup_supplier_name: '', password: '' });
  const [search, setSearch] = useState('');

  useEffect(() => { fetchUsers(); fetchSuppliers(); }, []);
  const fetchUsers = async () => { try { const { data } = await api.get('/admin/pickup-users'); setUsers(data); } catch {} };
  const fetchSuppliers = async () => { try { const { data } = await api.get('/admin/suppliers'); setSuppliers(data.filter(s => s.status !== 'inactive')); } catch {} };
  const resetForm = () => { setForm({ name: '', mobile: '', pickup_supplier_name: '', password: '' }); setEditing(null); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/pickup-users/${editing.id}`, {
          name: form.name, mobile: form.mobile, pickup_supplier_name: form.pickup_supplier_name,
          password: form.password || undefined,
        });
      } else {
        await api.post('/admin/pickup-users', form);
      }
      fetchUsers();
      setDialogOpen(false);
      resetForm();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? users.filter(u => [u.name, u.mobile, u.pickup_supplier_name].some(v => (v || '').toLowerCase().includes(q)))
    : users;

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name / mobile / supplier..." className="rounded-none md:max-w-sm" data-testid="pickup-user-search" />
        <div className="flex gap-2">
          <ExportButton rows={filtered} columns={PICKUP_USER_COLUMNS} filename="pickup_users" testId="export-pickup-users-btn" />
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild><Button data-testid="add-pickup-user-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}><Plus size={16} className="mr-2" />Add Pickup User</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>{editing ? 'Edit Pickup User' : 'Add Pickup User'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div><Label className="text-xs uppercase tracking-widest">Name *</Label><Input data-testid="pickup-user-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-none" required /></div>
                <div><Label className="text-xs uppercase tracking-widest">Mobile *</Label><Input data-testid="pickup-user-mobile-input" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} className="rounded-none" required placeholder="Used as default login password" /></div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Supplier *</Label>
                  <Input
                    list="supplier-list-for-pickup"
                    data-testid="pickup-user-supplier-input"
                    value={form.pickup_supplier_name}
                    onChange={e => setForm({...form, pickup_supplier_name: e.target.value})}
                    className="rounded-none"
                    required
                    placeholder="Select from supplier master"
                  />
                  <datalist id="supplier-list-for-pickup">
                    {suppliers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </datalist>
                  <p className="text-[10px] text-[#666] mt-1">This user will only see trips booked for this supplier.</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">
                    {editing ? 'New Password (leave blank to keep current)' : 'Password'}
                  </Label>
                  <Input type="password" data-testid="pickup-user-password-input" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="rounded-none" placeholder={editing ? 'Leave blank to keep current' : 'Defaults to mobile number if blank'} />
                </div>
                <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button><Button data-testid="pickup-user-save-btn" type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <p className="text-[#666] text-xs">{q ? `${filtered.length} match of ${users.length}` : `${users.length} pickup users`}</p>
      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader><TableRow className="bg-[#F3F3F3]">
            <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Mobile</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Supplier</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((u, i) => (<TableRow key={u.id} className={i%2?'bg-[#F3F3F3]/30':''}>
              <TableCell className="font-medium">{u.name}</TableCell>
              <TableCell>{u.mobile}</TableCell>
              <TableCell>{u.pickup_supplier_name}</TableCell>
              <TableCell><StatusBadge status={u.status} /></TableCell>
              <TableCell className="text-right">
                <button onClick={() => { setEditing(u); setForm({ name: u.name, mobile: u.mobile || '', pickup_supplier_name: u.pickup_supplier_name || '', password: '' }); setDialogOpen(true); }} className="p-2 hover:bg-[#F3F3F3]" data-testid={`edit-pickup-user-${u.id}`}><PencilSimple size={16} /></button>
                <ToggleBtn id={u.id} status={u.status} masterType="pickup-users" onDone={fetchUsers} />
                <SuperAdminDeleteButton masterType="pickup-users" recordId={u.id} recordName={u.name} onDeleted={fetchUsers} />
              </TableCell>
            </TableRow>))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#666] py-8">{q ? 'No pickup users match your search' : 'No pickup users yet'}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function PickupUsersPage() {
  return (
    <div data-testid="pickup-users-page" className="space-y-6">
      <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Pickup Users</h1>
      <PickupUsersTab />
    </div>
  );
}


// ===== WAITING RULES =====
function WaitingRulesTab() {
  const [rules, setRules] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ item: '', uom: '', from_qty: 0, to_qty: 0, allowed_time_minutes: 20 });
  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try { const [r, i, u] = await Promise.all([api.get('/admin/waiting-rules'), api.get('/admin/items'), api.get('/admin/uom')]); setRules(r.data); setItems(i.data.filter(x => x.status !== 'inactive')); setUoms(u.data.filter(x => x.status !== 'inactive')); } catch {}
  };
  const resetForm = () => { setForm({ item: '', uom: '', from_qty: 0, to_qty: 0, allowed_time_minutes: 20 }); setEditing(null); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) { await api.put(`/admin/waiting-rules/${editing.id}`, form); }
    else { await api.post('/admin/waiting-rules', form); }
    fetchAll(); setDialogOpen(false); resetForm();
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-[#666]">Default waiting time: 20 minutes (if no rule defined)</p>
      <div className="flex justify-end gap-2">
        <ExportButton rows={rules} columns={WAITING_RULES_COLUMNS} filename="waiting_rules" testId="export-waiting-rules-btn" />
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-rule-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}><Plus size={16} className="mr-2" />Add Rule</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>{editing ? 'Edit Waiting Rule' : 'Add Waiting Time Rule'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label className="text-xs uppercase tracking-widest">Item</Label><Input value={form.item} onChange={e => setForm({...form, item: e.target.value})} className="rounded-none" list="items-list" /><datalist id="items-list">{items.map(i => <option key={i.id} value={i.name} />)}</datalist></div>
              <div><Label className="text-xs uppercase tracking-widest">UOM</Label><Input value={form.uom} onChange={e => setForm({...form, uom: e.target.value})} className="rounded-none" list="uom-list" /><datalist id="uom-list">{uoms.map(u => <option key={u.id || u.name} value={u.name} />)}</datalist></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-widest">From Qty</Label><Input type="number" value={form.from_qty} onChange={e => setForm({...form, from_qty: parseFloat(e.target.value)||0})} className="rounded-none" /></div>
                <div><Label className="text-xs uppercase tracking-widest">To Qty</Label><Input type="number" value={form.to_qty} onChange={e => setForm({...form, to_qty: parseFloat(e.target.value)||0})} className="rounded-none" /></div>
              </div>
              <div><Label className="text-xs uppercase tracking-widest">Allowed Time (minutes)</Label><Input type="number" value={form.allowed_time_minutes} onChange={e => setForm({...form, allowed_time_minutes: parseInt(e.target.value)||20})} className="rounded-none" /></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button><Button type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E5E5E5]">
        <Table><TableHeader><TableRow className="bg-[#F3F3F3]"><TableHead className="text-xs uppercase tracking-widest">Item</TableHead><TableHead className="text-xs uppercase tracking-widest">UOM</TableHead><TableHead className="text-xs uppercase tracking-widest text-right">From Qty</TableHead><TableHead className="text-xs uppercase tracking-widest text-right">To Qty</TableHead><TableHead className="text-xs uppercase tracking-widest text-right">Allowed (min)</TableHead><TableHead className="text-xs uppercase tracking-widest">Status</TableHead><TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{rules.map((r, i) => (<TableRow key={r.id} className={i%2?'bg-[#F3F3F3]/30':''}>
            <TableCell>{r.item}</TableCell><TableCell>{r.uom}</TableCell><TableCell className="text-right">{r.from_qty}</TableCell><TableCell className="text-right">{r.to_qty}</TableCell><TableCell className="text-right font-medium">{r.allowed_time_minutes}</TableCell>
            <TableCell><StatusBadge status={r.status} /></TableCell>
            <TableCell className="text-right">
              <button onClick={() => { setEditing(r); setForm({ item: r.item, uom: r.uom, from_qty: r.from_qty, to_qty: r.to_qty, allowed_time_minutes: r.allowed_time_minutes }); setDialogOpen(true); }} className="p-2 hover:bg-[#F3F3F3]"><PencilSimple size={16} /></button>
              <ToggleBtn id={r.id} status={r.status} masterType="waiting-rules" onDone={fetchAll} />
              <SuperAdminDeleteButton masterType="waiting-rules" recordId={r.id} recordName={`${r.item} ${r.from_qty}-${r.to_qty}${r.uom}`} onDeleted={fetchAll} />
            </TableCell></TableRow>))}
            {rules.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-[#666] py-8">No rules defined (default: 20 min)</TableCell></TableRow>}
          </TableBody></Table>
      </div>
    </div>
  );
}

// ===== MAIN EXPORTS =====
export function ItemsPage() { return <div data-testid="items-page" className="space-y-6"><h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Items</h1><ItemsTab /></div>; }
export function UomPage() { return <div data-testid="uom-page" className="space-y-6"><h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Units of Measure</h1><UomTab /></div>; }
export function LicenseTypesPage() { return <div data-testid="license-types-page" className="space-y-6"><h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">License Types</h1><LicenseTypesTab /></div>; }
export function LoadmenPage() { return <div data-testid="loadmen-page" className="space-y-6"><h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Helpers</h1><LoadmenTab /></div>; }
export function StaffPage() { return <div data-testid="staff-page" className="space-y-6"><h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Booking</h1><StaffTab /></div>; }
export function WaitingRulesPage() { return <div data-testid="waiting-rules-page" className="space-y-6"><h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Waiting Time Rules</h1><WaitingRulesTab /></div>; }


// ===== Phase 78y — PICKUP / DROP TIMINGS (vehicle-type aware) =====
function PickupDropTimingsTab() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { vehicle_type: '', item: '', uom: '', qty_from: 0, qty_to: 0, pickup_mins: 20, drop_mins: 20 };
  const [form, setForm] = useState(empty);

  const fetchAll = async () => {
    try {
      const [r, i, u] = await Promise.all([
        api.get('/admin/pickup-drop-timings'),
        api.get('/admin/items'),
        api.get('/admin/uom'),
      ]);
      setRows(r.data || []);
      setItems((i.data || []).filter(x => x.status !== 'inactive'));
      setUoms((u.data || []).filter(x => x.status !== 'inactive'));
    } catch {}
  };
  useEffect(() => { fetchAll(); }, []);

  const reset = () => { setForm(empty); setEditing(null); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/pickup-drop-timings/${editing.id}`, form);
      else await api.post('/admin/pickup-drop-timings', form);
      setDialogOpen(false); reset(); fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this timing row?')) return;
    await api.delete(`/admin/pickup-drop-timings/${id}`);
    fetchAll();
  };

  return (
    <div className="space-y-4" data-testid="pickup-drop-timings-tab">
      <p className="text-sm text-[#666]">
        Default if no row matches: <span className="font-bold text-[#0A0A0A]">20 minutes</span> for pickup and drop.
        Used by the Booking dashboard's <span className="font-bold">Allocated</span> tab to project the pilot's expected
        pickup-reach time (Google Routes + this allowed wait).
      </p>
      <div className="flex justify-end">
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-timing-btn"
              className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none"
              onClick={reset}
            >
              <Plus size={16} className="mr-2" />Add Row
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Timing Row' : 'Add Pickup/Drop Timing Row'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Vehicle Type *</Label>
                <Input
                  value={form.vehicle_type}
                  onChange={e => setForm({ ...form, vehicle_type: e.target.value })}
                  className="rounded-none" required
                  placeholder="e.g. BOLERO, TATA-ACE, EICHER"
                  data-testid="timing-vehicle-type"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Item (optional — blank = any)</Label>
                <Input
                  value={form.item}
                  onChange={e => setForm({ ...form, item: e.target.value })}
                  className="rounded-none" list="timing-items"
                  data-testid="timing-item"
                />
                <datalist id="timing-items">{items.map(i => <option key={i.id} value={i.name} />)}</datalist>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">UOM (optional — blank = any)</Label>
                <Input
                  value={form.uom}
                  onChange={e => setForm({ ...form, uom: e.target.value })}
                  className="rounded-none" list="timing-uoms"
                  data-testid="timing-uom"
                />
                <datalist id="timing-uoms">{uoms.map(u => <option key={u.id || u.name} value={u.name} />)}</datalist>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Qty From</Label>
                  <Input
                    type="number" step="any"
                    value={form.qty_from}
                    onChange={e => setForm({ ...form, qty_from: parseFloat(e.target.value) || 0 })}
                    className="rounded-none"
                    data-testid="timing-qty-from"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Qty To</Label>
                  <Input
                    type="number" step="any"
                    value={form.qty_to}
                    onChange={e => setForm({ ...form, qty_to: parseFloat(e.target.value) || 0 })}
                    className="rounded-none"
                    data-testid="timing-qty-to"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Pickup (mins)</Label>
                  <Input
                    type="number"
                    value={form.pickup_mins}
                    onChange={e => setForm({ ...form, pickup_mins: parseInt(e.target.value, 10) || 0 })}
                    className="rounded-none"
                    data-testid="timing-pickup-mins"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Drop (mins)</Label>
                  <Input
                    type="number"
                    value={form.drop_mins}
                    onChange={e => setForm({ ...form, drop_mins: parseInt(e.target.value, 10) || 0 })}
                    className="rounded-none"
                    data-testid="timing-drop-mins"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); reset(); }} className="rounded-none">Cancel</Button>
                <Button
                  type="submit"
                  className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none"
                  data-testid="timing-save-btn"
                >
                  {editing ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Vehicle Type</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Item</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">UOM</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Qty From</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Qty To</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Pickup (min)</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Drop (min)</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.id} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`timing-row-${r.id}`}>
                <TableCell className="font-medium">{r.vehicle_type}</TableCell>
                <TableCell>{r.item || <span className="text-[#999]">any</span>}</TableCell>
                <TableCell>{r.uom || <span className="text-[#999]">any</span>}</TableCell>
                <TableCell className="text-right">{r.qty_from}</TableCell>
                <TableCell className="text-right">{r.qty_to}</TableCell>
                <TableCell className="text-right font-medium">{r.pickup_mins}</TableCell>
                <TableCell className="text-right font-medium">{r.drop_mins}</TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => {
                      setEditing(r);
                      setForm({
                        vehicle_type: r.vehicle_type || '', item: r.item || '', uom: r.uom || '',
                        qty_from: r.qty_from ?? 0, qty_to: r.qty_to ?? 0,
                        pickup_mins: r.pickup_mins ?? 20, drop_mins: r.drop_mins ?? 20,
                      });
                      setDialogOpen(true);
                    }}
                    className="p-2 hover:bg-[#F3F3F3]"
                    data-testid={`timing-edit-${r.id}`}
                  >
                    <PencilSimple size={16} />
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    className="p-2 hover:bg-[#FF3333]/10 text-[#FF3333]"
                    data-testid={`timing-delete-${r.id}`}
                    title="Delete"
                  >
                    <Power size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[#666] py-8">
                  No rows yet — defaults to 20 minutes pickup and drop for every trip.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function PickupDropTimingsPage() {
  return (
    <div data-testid="pickup-drop-timings-page" className="space-y-6">
      <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Pickup &amp; Drop Timings</h1>
      <PickupDropTimingsTab />
    </div>
  );
}


// ===== Phase 78ac — TRANSLATIONS (Admin manages Tamil overrides) =====
export function TranslationsPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');

  const fetchRows = async (search = '') => {
    try {
      const { data } = await api.get('/translate/cache', { params: { target: 'ta', q: search } });
      setRows(data || []);
    } catch {}
  };
  useEffect(() => { fetchRows(); }, []);

  const save = async () => {
    if (!editing || !draft.trim()) return;
    try {
      await api.put(`/translate/cache/${editing.key}`, { translated: draft.trim() });
      setEditing(null);
      setDraft('');
      fetchRows(q);
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete Tamil translation for "${row.source}"?\nIt will be re-translated by AI on next pilot view.`)) return;
    try {
      await api.delete(`/translate/cache/${row.key}`);
      fetchRows(q);
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="translations-page" className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Tamil Translations</h1>
          <p className="text-sm text-[#666] mt-1 max-w-2xl">
            Every supplier / booker / item / halt / remark name auto-translated for the Pilot app's
            <span className="font-bold"> Complete Tamil </span> mode is recorded here. Edit any row to fix
            an awkward AI translation — the new value is saved back to the matching supplier/user master
            and the pilot picks it up on next refresh. Deleting a row forces a re-translate on next view.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Input
          placeholder="Search English or Tamil..."
          value={q}
          onChange={e => { setQ(e.target.value); fetchRows(e.target.value); }}
          className="max-w-sm rounded-none"
          data-testid="translation-search"
        />
        <span className="text-xs text-[#666]">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
      </div>
      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">English (source)</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Tamil (translated)</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.key} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`tr-row-${r.key}`}>
                <TableCell className="font-medium">{r.source}</TableCell>
                <TableCell>
                  {editing?.key === r.key ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        className="rounded-none flex-1"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(null); setDraft(''); } }}
                        data-testid={`tr-edit-input-${r.key}`}
                      />
                      <Button onClick={save} size="sm" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" data-testid={`tr-save-${r.key}`}>Save</Button>
                      <Button onClick={() => { setEditing(null); setDraft(''); }} size="sm" variant="outline" className="rounded-none">Cancel</Button>
                    </div>
                  ) : (
                    <span className="font-medium text-[#5B21B6]">{r.translated}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => { setEditing(r); setDraft(r.translated); }}
                    className="p-2 hover:bg-[#F3F3F3]"
                    data-testid={`tr-edit-${r.key}`}
                    title="Edit translation"
                  >
                    <PencilSimple size={16} />
                  </button>
                  <button
                    onClick={() => remove(r)}
                    className="p-2 hover:bg-[#FF3333]/10 text-[#FF3333]"
                    data-testid={`tr-delete-${r.key}`}
                    title="Delete (re-translate on next view)"
                  >
                    <Power size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-[#666] py-8">
                  {q ? 'No matches.' : 'No translations yet. They appear here as pilots use Complete Tamil mode.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
